import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, Lock, RotateCcw, ShieldCheck } from 'lucide-react';
import { rbacOperations, type RbacOperation } from '@/api/client';
import { humanRole } from '@/lib/humanise';
import { RBAC_ROLE_GROUPS } from '@/lib/rbacModel';
import { useAuth } from '@/contexts/AuthContext';
import { errorText } from '@/lib/permissionError';

/**
 * The RBAC grid — role x module x Create/Read/Update/Delete, per site.
 *
 * This is the surface the client actually thinks in, and it is the one that
 * saves and enforces. The Operations tab is still there for precision, but
 * nobody should need to read `studies.ie_checklist.deactivate` to give
 * Coordinators delete on Studies.
 *
 * ONE CELL CAN COVER SEVERAL OPERATIONS.
 * (studies, create) holds studies.create, studies.ie_checklist.create and
 * studies.schedule.visit.create. Ticking the cell sets all three; when they
 * disagree — which they do out of the box, because the code gave them
 * different answers — the cell shows as MIXED rather than silently picking a
 * winner. Clicking a mixed cell grants all of them; clicking again revokes all.
 *
 * READ IS IMPLIED. The server adds Read on any module where a role gains a
 * verb, so the save can come back having touched more operations than were
 * ticked. The grid re-reads afterwards rather than guessing.
 */

type CellState = 'all' | 'some' | 'none';

const VERBS = ['create', 'read', 'update', 'delete'] as const;

const MODULE_LABELS: Record<string, string> = {
  studies: 'Studies',
  subjects: 'Subjects',
  visits: 'Visit Calendar',
  queries: 'Queries',
  binder: 'eISF Binder',
  delegations: 'Delegations',
  sponsors: 'Sponsors',
  master_data: 'Master Data',
};

/**
 * What a cell actually gates, where the CRUD word alone misleads.
 *
 * The binder row is the reason this exists. "Read" does NOT mean "can open
 * the binder" — the binder, folder and preview endpoints resolve through
 * resolveBinderRead, which the `read redacted` cell drives. `binder.read`
 * only covers the secondary listings. Someone reading the grid would
 * reasonably conclude the opposite.
 */
const CELL_HINTS: Record<string, Partial<Record<string, string>>> = {
  binder: {
    create: 'Add a document slot to a folder (the "New document" button).',
    read: 'Search, My Files and Site Documents. NOT opening the binder itself — '
      + 'that is governed by "read redacted" below.',
    update: 'Re-tag, rename, reassign, and move a version through its lifecycle. '
      + 'Attaching the file is "upload".',
    upload: 'Attach a file to a document slot.',
    read_redacted: 'Open the binder. Monitors and auditors see it with PHI redacted.',
  },
};

function cellTitle(module: string, verb: string, role: string): string {
  const base = `${humanRole(role)} — ${label(module)} — ${verb}`;
  const hint = CELL_HINTS[module]?.[verb];
  return hint ? `${base}

${hint}` : base;
}

function label(module: string): string {
  return MODULE_LABELS[module] ?? module.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function RbacGridPanel() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [role, setRole] = useState<string>('site_admin');
  /** operation_key -> roles, for operations edited this session. */
  const [draft, setDraft] = useState<Record<string, string[]>>({});

  const q = useQuery({
    queryKey: ['rbac-operations'],
    queryFn: () => rbacOperations.list(),
    staleTime: 15_000,
  });

  const ops = useMemo(() => q.data?.operations ?? [], [q.data]);

  // Show the site's NAME. The id is what the API keys on, but it tells a
  // reader nothing — and this banner is the one place someone checks they
  // are about to change the right site's permissions.
  const siteId = q.data?.site_id;
  const siteLabel = siteId
    ? (user?.siteNames?.[siteId] ?? `site ${siteId}`)
    : 'this site';

  /** Effective roles for an operation, with this session's edits applied. */
  const rolesOf = (o: RbacOperation): string[] => draft[o.operation_key] ?? o.effective_roles;

  /** module -> verb -> the operations in that cell. */
  const cells = useMemo(() => {
    const m: Record<string, Record<string, RbacOperation[]>> = {};
    for (const o of ops) {
      (m[o.module] ??= {});
      (m[o.module]![o.verb] ??= []).push(o);
    }
    return m;
  }, [ops]);

  const modules = useMemo(() => Object.keys(cells).sort(), [cells]);

  /** Extra verbs beyond CRUD that this module actually uses. */
  const extraVerbs = (module: string): string[] =>
    Object.keys(cells[module] ?? {})
      .filter((v) => !(VERBS as readonly string[]).includes(v))
      .sort();

  function stateOf(module: string, verb: string): CellState {
    const group = cells[module]?.[verb];
    if (!group || group.length === 0) return 'none';
    const held = group.filter((o) => rolesOf(o).includes(role)).length;
    if (held === 0) return 'none';
    return held === group.length ? 'all' : 'some';
  }

  /**
   * Why this cell cannot be granted to the selected role, or null.
   *
   * Reported 2026-10-14: a platform admin ticked Subjects create/read/update
   * and was still refused, because subjects also require an approved DoA
   * delegation and an admin never has one. The tick was honest about the
   * registry and a lie about the system.
   *
   * Blocked only when EVERY operation behind the cell is ungrantable. A cell
   * covering several operations where one is still grantable stays live, since
   * ticking it does have an effect.
   */
  function blockedReason(module: string, verb: string): string | null {
    const group = cells[module]?.[verb];
    if (!group || group.length === 0) return null;
    const reasons = group.map((o) => o.ungrantable?.[role]).filter(Boolean) as string[];
    return reasons.length === group.length ? reasons[0]! : null;
  }

  function toggle(module: string, verb: string) {
    const group = cells[module]?.[verb];
    if (!group || group.length === 0) return;
    // Belt and braces - the control is not rendered as clickable, but a
    // keyboard or stale render must not slip a dead role through either.
    if (blockedReason(module, verb)) return;
    // Mixed or empty -> grant everything in the cell. Full -> revoke it.
    const grant = stateOf(module, verb) !== 'all';
    setDraft((d) => {
      const next = { ...d };
      for (const o of group) {
        const current = next[o.operation_key] ?? o.effective_roles;
        next[o.operation_key] = grant
          ? [...new Set([...current, role])]
          : current.filter((r) => r !== role);
      }
      return next;
    });
  }

  const pending = useMemo(
    () =>
      ops
        .filter((o) => {
          const d = draft[o.operation_key];
          if (!d) return false;
          const a = [...d].sort().join('|');
          const b = [...o.effective_roles].sort().join('|');
          return a !== b;
        })
        .map((o) => ({ operation_key: o.operation_key, roles: draft[o.operation_key]! })),
    [ops, draft],
  );

  const saveM = useMutation({
    mutationFn: () => rbacOperations.save(pending),
    onSuccess: (res) => {
      const changed = res.applied.filter((a) => a.action !== 'unchanged');
      const extra = changed.length - pending.length;
      toast.success(
        changed.length === 0
          ? 'No changes to apply.'
          : `Applied — in force now.${extra > 0 ? ` Read was granted on ${extra} more operation${extra === 1 ? '' : 's'}.` : ''}`,
      );
      setDraft({});
      void qc.invalidateQueries({ queryKey: ['rbac-operations'] });
    },
    onError: (e: unknown) =>
      toast.error(errorText(e, 'Could not apply the changes.')),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded border border-emerald-300 bg-emerald-50 px-4 py-3">
        <ShieldCheck className="h-4 w-4 text-emerald-700 mt-0.5 shrink-0" />
        <div className="text-sm text-emerald-900">
          <p className="font-semibold">
            This grid is enforced — for {siteLabel}.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-ink-muted">
          {modules.length} module{modules.length === 1 ? '' : 's'} · {ops.length} operations
          {pending.length > 0 && (
            <span className="ml-2 font-semibold text-violet-700">
              · {pending.length} unsaved change{pending.length === 1 ? '' : 's'}
            </span>
          )}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn-secondary"
            disabled={pending.length === 0 || saveM.isPending}
            onClick={() => setDraft({})}
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Discard changes
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={pending.length === 0 || saveM.isPending}
            onClick={() => saveM.mutate()}
          >
            {saveM.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <CheckCircle2 className="h-3.5 w-3.5" />
            )}
            Save and apply
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-6">
        {/* Role picker */}
        <nav className="rounded border border-surface-border bg-white overflow-hidden self-start">
          {RBAC_ROLE_GROUPS.map((group) => (
            <div key={group.label}>
              <div className="px-3 py-2 bg-surface-muted/40 border-b border-surface-border">
                <p className="text-[11px] uppercase tracking-wider text-ink-muted font-semibold">
                  {group.label}
                </p>
              </div>
              {group.roles.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  className={[
                    'w-full text-left px-3 py-2 text-sm border-b border-surface-border/60',
                    r === role ? 'bg-navy-50 text-ink font-medium' : 'hover:bg-surface-muted/40 text-ink',
                  ].join(' ')}
                >
                  {humanRole(r)}
                </button>
              ))}
            </div>
          ))}
        </nav>

        {/* The grid for the selected role */}
        <div className="rounded border border-surface-border bg-white overflow-hidden min-w-0">
          <header className="px-4 py-3 border-b border-surface-border">
            <h2 className="text-base font-semibold text-ink">{humanRole(role)}</h2>
            <p className="text-xs text-ink-muted mt-0.5">
              What this role may do at this site. A half-filled box means the operations behind
              that cell disagree — clicking grants all of them.
            </p>
          </header>

          {q.isLoading ? (
            <div className="p-10 text-center">
              <Loader2 className="h-4 w-4 animate-spin mx-auto text-ink-muted" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface-muted/40 text-left text-[11px] uppercase tracking-wider text-ink-muted border-b border-surface-border">
                  <tr>
                    <th className="px-4 py-2 min-w-[180px]">Module</th>
                    {VERBS.map((v) => (
                      <th key={v} className="px-2 py-2 text-center w-[84px]">
                        {v}
                      </th>
                    ))}
                    <th className="px-4 py-2">Also</th>
                  </tr>
                </thead>
                <tbody>
                  {modules.map((m) => (
                    <tr key={m} className="border-b border-surface-border/60 last:border-0">
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-ink">{label(m)}</div>
                        <div className="text-[11px] text-ink-muted font-mono">{m}</div>
                      </td>
                      {VERBS.map((v) => (
                        <td key={v} className="px-2 py-2.5 text-center">
                          <CellBox
                            blocked={blockedReason(m, v)}
                            state={stateOf(m, v)}
                            disabled={!cells[m]?.[v]}
                            onClick={() => toggle(m, v)}
                            title={cellTitle(m, v, role)}
                          />
                        </td>
                      ))}
                      <td className="px-4 py-2.5">
                        <div className="flex flex-wrap gap-2">
                          {extraVerbs(m).map((v) => (
                            <label
                              key={v}
                              className="flex items-center gap-1 text-[11px] text-ink cursor-pointer"
                            >
                              <CellBox
                                blocked={blockedReason(m, v)}
                                state={stateOf(m, v)}
                                onClick={() => toggle(m, v)}
                                title={cellTitle(m, v, role)}
                                small
                              />
                              {v.replace(/_/g, ' ')}
                            </label>
                          ))}
                          {extraVerbs(m).length === 0 && (
                            <span className="text-[11px] text-ink-muted">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CellBox({
  state,
  onClick,
  disabled,
  blocked,
  title,
  small,
}: {
  state: CellState;
  onClick: () => void;
  disabled?: boolean;
  /** Set when the operation EXISTS but this role could never hold it. */
  blocked?: string | null;
  title?: string;
  small?: boolean;
}) {
  const size = small ? 'h-3.5 w-3.5' : 'h-4 w-4';
  if (blocked) {
    // Distinct from the dash below, and the distinction matters. A dash means
    // "this module has no such operation". A padlock means "the operation is
    // real, this role simply cannot hold it" - ticking it would be overruled
    // by a gate the grid does not control, so the tick is not offered.
    return (
      <span
        className={`inline-flex ${size} items-center justify-center text-ink-muted/70 select-none`}
        title={blocked}
        aria-label={`cannot be granted: ${blocked}`}
      >
        <Lock className="h-3 w-3" aria-hidden />
      </span>
    );
  }
  if (disabled) {
    // A dash, not an empty box. An unticked box and a greyed box look almost
    // identical, so "this module has no such operation" read as "this role is
    // not allowed to" — eISF Binder has no delete at all (documents are
    // superseded, never deleted), and the blank cell suggested otherwise.
    return (
      <span
        className={`inline-flex ${size} items-center justify-center text-ink-muted/60 select-none`}
        title="No such operation on this module — nothing to grant"
        aria-label="not applicable"
      >
        —
      </span>
    );
  }
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      aria-pressed={state === 'all'}
      className={[
        `inline-flex items-center justify-center ${size} rounded border transition-colors`,
        state === 'all'
          ? 'bg-navy-600 border-navy-600 text-white'
          : state === 'some'
            ? 'bg-violet-100 border-violet-400'
            : 'bg-white border-surface-border hover:border-navy-600',
      ].join(' ')}
    >
      {state === 'all' && <span className="text-[10px] leading-none">✓</span>}
      {/* Half-filled bar reads as "these disagree" without implying on or off. */}
      {state === 'some' && <span className="block w-2 h-0.5 bg-violet-600 rounded" />}
    </button>
  );
}
